import React, { Fragment, useEffect, useState, memo } from "react";
import { Row, Col, Card } from "react-bootstrap";
import Chart from "react-apexcharts";
import DashboardService from "../../../services/pbumku/DashboardService";

// Format tanggal
const formatDate = (date, isEndOfDay = false) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const time = isEndOfDay ? '23:59:59' : '00:00:00';
  return `${yyyy}-${mm}-${dd} ${time}`;
};

const getStartOfYear = () => new Date(new Date().getFullYear(), 0, 1);
const getToday = () => new Date();

// Komponen chart per sertifikat
const JenisSertifikatChart = ({ title, total, data = [] }) => {
  if (!Array.isArray(data) || data.length === 0) return null;

  const getColorByTitle = (title) => {
    switch (title) {
      case "CPIB": return "#0071ce";
      case "CBIB": return "#004d40";
      case "CPOIB": return "#f7941e";
      case "CDOIB": return "#003166";
      case "CPPIB": return "#e74c3c";
      case "CPIB Kapal": return "#f1c40f";
      default: return "#95a5a6";
    }
  };

  const options = {
    chart: {
      type: "bar",
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "60%",
        dataLabels: {
          position: "center",
        },
      },
    },
    dataLabels: {
      enabled: true,
      position: "center",
      formatter: (val) => val,
      style: {
        colors: ["#fff"],
        fontSize: "14px"
      },
    },
    xaxis: {
      categories: data.map((d) => d.category),
      labels: {
        style: {
          fontSize: "10px",
          whiteSpace: "normal",
        },
      },
    },
    yaxis: {
      labels: {
        show: true,
        style: {
          fontSize: "13px",
          colors: ["#333"],
        },
        maxWidth: 180,
        formatter: function (val) {
          return val.length > 20 ? val.match(/.{1,20}/g).join('\n') : val;
        },
      },
    },

    tooltip: { enabled: true },
    grid: { show: false },
    colors: [getColorByTitle(title)],
  };

  const series = [{ name: "Jumlah", data: data.map((d) => d.value) }];

  return (
    <Card className="mb-1 shadow rounded-4" style={{ backgroundColor: "#fff", minHeight: "300px" }}>
      <Card.Body>
       <div className="rounded py-2 text-white text-center mb-2" style={{ backgroundColor: getColorByTitle(title) }}>
          <h5 className="fw-bold text-center text-white">
            Top 5 Provinsi Sertifikasi {title}
          </h5>
        </div>
        <Chart options={options} series={series} type="bar" height={220} />
        <div className="mt-1 text-center bg-info text-white py-2 rounded">
          <strong>Total Sertifikat Terbit: {total}</strong>
        </div>
      </Card.Body>
    </Card>
  );
};

// Komponen utama
const ReportUmku = memo(() => {
  const dashService = new DashboardService();
  const [jenisData, setJenisData] = useState([]);
  const [startDateUmku] = useState(getStartOfYear());
  const [endDateUmku] = useState(getToday());

  const jenisIzinList = [
    { title: "CPIB", kd_izin: "032000000034" },
    { title: "CBIB", kd_izin: "032000000068" },
    { title: "CPPIB", kd_izin: "032000000014" },
    { title: "CPOIB", kd_izin: "032000000019" },
    { title: "CDOIB", kd_izin: "032000000036" },
    { title: "CPIB Kapal", kd_izin: "032000000033" },
  ];

  useEffect(() => {
    fetchAllJenisData();
  }, []);

  const fetchAllJenisData = async () => {
    const allData = [];

    for (const izin of jenisIzinList) {
      try {
        const response = await dashService.getProvinsiPerIzin(
          formatDate(startDateUmku),
          formatDate(endDateUmku, true),
          izin.kd_izin
        );

        const dataRaw = response.data?.data || [];

        const data = dataRaw
          .map((item) => ({
            category: item.URAIAN_PROPINSI,
            value: item.Jumlah,
          }))
          .sort((a, b) => b.value - a.value) 
          .slice(0, 5); //  5 provinsi teratas

        const total = data.reduce((sum, item) => sum + item.value, 0);

        allData.push({
          title: izin.title,
          total,
          data,
        });
      } catch (err) {
        console.error(`Gagal fetch ${izin.title}:`, err);
      }
    }

    setJenisData(allData);
  };

  return (
    <Fragment>
      <Row>
        <Col md="6" lg="12">
          <div className="iq-navbar-header mt-1 mb-1 px-4 py-3 rounded bg-primary">
            <Row className="align-items-center">
              <Col lg={6}>
                <h2 className="text-white fw-bold">Statistik Sertifikasi Mutu Primer</h2>
                <h3 className="text-white">Tahun {new Date().getFullYear()}</h3>
              </Col>
            </Row>
          </div>
        </Col>
      </Row>

      <Row className="gy-3">
          {jenisData.map((item, index) => (
            <Col xs={12} sm={6} lg={4} key={index}>
              <JenisSertifikatChart
                title={item.title}
                total={item.total}
                data={item.data}
              />
            </Col>
          ))}
        </Row>
    </Fragment>
  );
});

export default ReportUmku;
